import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { format, parse } from 'date-fns';
import { getUrlParams } from '../common';
import { trainers } from '../constants';
import Logo from '../assets/Logo.svg';
import Input from './Input';
import Radio from './Radio';
import Checkbox from './Checkbox';
import { clearPageMessage, showLoadingMessage, showSuccessMessage, showWarningMessage } from '../successMessage';

const iCanLabels = [
    'Write a SMS message',
    'Check change when shopping',
    'Read a TV guide',
    'Read an email',
    'Read a map',
    'Write a job application',
    'Calculate fractions, decimals and percentages',
    'Write a work report',
    'Read the prompts on an ATM',
    'Use a calculator',
    'Read an account or bill',
];

const incorrectWords = ['Mondey', 'Bus', 'Pensil', 'Shoes', 'Food', 'Phone', 'Cleen', 'House', 'Play', 'Tecnical'];

const questionTitleStyles = {
    display: 'block',
    marginBottom: '14px',
};

const inlineErrorStyles = {
    margin: '6px 0 0',
    fontSize: '13px',
    color: 'red',
    fontWeight: '600',
};

const QldLlnStep = ({
    onComplete,
    onProgress,
    initialData = {},
    trainerNameOverride = '',
    deliveryOverride = '',
    studentNameOverride = '',
    trainerMode = false,
    hideTrainerDecisionActions = false,
}) => {
    const [errors, setErrors] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    const [studentName, setStudentName] = useState(initialData.studentName || studentNameOverride || '');
    const [trainingCourse, setTrainingCourse] = useState('CPCWHS1001 Prepare to Work Safely in the Construction Industry');
    const [dateFirstPage] = useState(
        initialData.dateFirstPage
            ? parse(initialData.dateFirstPage, 'dd/MM/yyyy', new Date())
            : new Date()
    );
    const [trainerName, setTrainerName] = useState(initialData.trainerName || trainerNameOverride || '');
    const [delivery, setDelivery] = useState(initialData.delivery || deliveryOverride || '');
    const [iCan, setICan] = useState(initialData.iCan || ['', '', '', '', '', '', '', '', '', '', '']);
    const [alphabetWordOne, setAlphabetWordOne] = useState(initialData.alphabetWords?.[0] || '');
    const [alphabetWordTwo, setAlphabetWordTwo] = useState(initialData.alphabetWords?.[1] || '');
    const [alphabetWordThree, setAlphabetWordThree] = useState(initialData.alphabetWords?.[2] || '');
    const [alphabetWordFour, setAlphabetWordFour] = useState(initialData.alphabetWords?.[3] || '');
    const [alphabetWordFive, setAlphabetWordFive] = useState(initialData.alphabetWords?.[4] || '');
    const [alphabetWordSix, setAlphabetWordSix] = useState(initialData.alphabetWords?.[5] || '');
    const [chosenIncorrectWords, setChosenIncorrectWords] = useState(initialData.chosenIncorrectWords || []);
    const [whyInterested, setWhyInterested] = useState(initialData.whyInterested || '');
    const [grams, setGrams] = useState(initialData.grams || '');
    const [tenPercent, setTenPercent] = useState(initialData.tenPercent || '');
    const [oneMeter, setOneMeter] = useState(initialData.oneMeter || '');
    const [heartBeat, setHeartBeat] = useState(initialData.heartBeat || '');
    const [hazardEg, setHazardEg] = useState(initialData.hazardEg || '');
    const [fireSafety, setFireSafety] = useState(initialData.fireSafety || '');
    const [safetyImportance, setSafetyImportance] = useState(initialData.safetyImportance || '');
    const [oralDiscussion, setOralDiscussion] = useState(initialData.oralDiscussion || '');
    const [submitState, setSubmitState] = useState('idle');

    useEffect(() => {
        if (trainerNameOverride) {
            setTrainerName(trainerNameOverride);
        }

        if (deliveryOverride) {
            setDelivery(deliveryOverride);
        }

        if (studentNameOverride) {
            setStudentName(studentNameOverride);
        }
    }, [trainerNameOverride, deliveryOverride, studentNameOverride]);

    useEffect(() => {
        setStudentName(initialData.studentName || studentNameOverride || '');
        setTrainingCourse('CPCWHS1001 Prepare to Work Safely in the Construction Industry');
        setTrainerName(initialData.trainerName || trainerNameOverride || '');
        setDelivery(initialData.delivery || deliveryOverride || '');
        setICan(initialData.iCan || ['', '', '', '', '', '', '', '', '', '', '']);
        setAlphabetWordOne(initialData.alphabetWords?.[0] || '');
        setAlphabetWordTwo(initialData.alphabetWords?.[1] || '');
        setAlphabetWordThree(initialData.alphabetWords?.[2] || '');
        setAlphabetWordFour(initialData.alphabetWords?.[3] || '');
        setAlphabetWordFive(initialData.alphabetWords?.[4] || '');
        setAlphabetWordSix(initialData.alphabetWords?.[5] || '');
        setChosenIncorrectWords(initialData.chosenIncorrectWords || []);
        setWhyInterested(initialData.whyInterested || '');
        setGrams(initialData.grams || '');
        setTenPercent(initialData.tenPercent || '');
        setOneMeter(initialData.oneMeter || '');
        setHeartBeat(initialData.heartBeat || '');
        setHazardEg(initialData.hazardEg || '');
        setFireSafety(initialData.fireSafety || '');
        setSafetyImportance(initialData.safetyImportance || '');
        setOralDiscussion(initialData.oralDiscussion || '');
    }, [initialData, trainerNameOverride, deliveryOverride, studentNameOverride]);

    useEffect(() => {
        const params = getUrlParams();
        if ((!params.trainerId && !params.trainerid) && !trainerNameOverride) {
            toast.error('Trainer ID is required!!!', { position: 'top-center' });
            return;
        }

        const trainerId = params.trainerId || params.trainerid;
        const matchingTrainer = trainers.find((row) => row.trainerId === trainerId);
        if (matchingTrainer) {
            setTrainerName(matchingTrainer.name);
        }

        if (params.delivery) {
            setDelivery(params.delivery[0].toUpperCase() + params.delivery.substring(1));
        }
    }, [trainerNameOverride]);

    const buildPayload = () => ({
        studentName,
        trainingCourse,
        dateFirstPage: format(dateFirstPage, 'dd/MM/yyyy'),
        trainerName,
        delivery,
        iCan,
        alphabetWords: [
            alphabetWordOne,
            alphabetWordTwo,
            alphabetWordThree,
            alphabetWordFour,
            alphabetWordFive,
            alphabetWordSix,
        ],
        chosenIncorrectWords,
        whyInterested,
        grams,
        tenPercent,
        oneMeter,
        heartBeat,
        hazardEg,
        fireSafety,
        safetyImportance,
        oralDiscussion,
    });

    useEffect(() => {
        setSubmitState('idle');
    }, [
        studentName,
        trainingCourse,
        dateFirstPage,
        trainerName,
        delivery,
        iCan,
        alphabetWordOne,
        alphabetWordTwo,
        alphabetWordThree,
        alphabetWordFour,
        alphabetWordFive,
        alphabetWordSix,
        chosenIncorrectWords,
        whyInterested,
        grams,
        tenPercent,
        oneMeter,
        heartBeat,
        hazardEg,
        fireSafety,
        safetyImportance,
        oralDiscussion,
    ]);

    useEffect(() => {
        if (!onProgress) {
            return undefined;
        }

        const timeoutId = setTimeout(() => {
            onProgress(buildPayload());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [
        onProgress,
        studentName,
        trainingCourse,
        dateFirstPage,
        trainerName,
        delivery,
        iCan,
        alphabetWordOne,
        alphabetWordTwo,
        alphabetWordThree,
        alphabetWordFour,
        alphabetWordFive,
        alphabetWordSix,
        chosenIncorrectWords,
        whyInterested,
        grams,
        tenPercent,
        oneMeter,
        heartBeat,
        hazardEg,
        fireSafety,
        safetyImportance,
        oralDiscussion,
    ]);

    const validations = [
        { name: 'studentName', value: studentName },
        { name: 'trainingCourse', value: trainingCourse },
        { name: 'iCan', value: iCan, array: true },
        { name: 'alphabetWordOne', value: alphabetWordOne },
        { name: 'alphabetWordTwo', value: alphabetWordTwo },
        { name: 'alphabetWordThree', value: alphabetWordThree },
        { name: 'alphabetWordFour', value: alphabetWordFour },
        { name: 'alphabetWordFive', value: alphabetWordFive },
        { name: 'alphabetWordSix', value: alphabetWordSix },
        { name: 'chosenIncorrectWords', value: chosenIncorrectWords, array: true },
        { name: 'whyInterested', value: whyInterested },
        { name: 'grams', value: grams },
        { name: 'tenPercent', value: tenPercent },
        { name: 'oneMeter', value: oneMeter },
        { name: 'heartBeat', value: heartBeat },
        { name: 'hazardEg', value: hazardEg },
        { name: 'fireSafety', value: fireSafety },
        { name: 'safetyImportance', value: safetyImportance },
    ];

    const toggleIncorrectWords = (word, value) => {
        if (value && !chosenIncorrectWords.includes(word)) {
            setChosenIncorrectWords([...chosenIncorrectWords, word]);
            return;
        }

        setChosenIncorrectWords(chosenIncorrectWords.filter((row) => row !== word));
    };

    const hasError = (fieldName) => errors.includes(fieldName);
    const renderInlineError = (fieldName, message = 'This field is required.') => (
        hasError(fieldName) ? <p style={inlineErrorStyles}>{message}</p> : null
    );

    const handleNext = async (decision = '') => {
        const tempErrors = [];

        validations.forEach((row) => {
            if (row.array) {
                if (row.value.length === 0 || row.value.filter((value) => !value).length > 0) {
                    tempErrors.push(row.name);
                }
                return;
            }

            if (!row.value) {
                tempErrors.push(row.name);
            }
        });

        if (tempErrors.length > 0) {
            setErrors(tempErrors);
            setErrorMessage('Please fill all the fields.');
            return;
        }

        setErrors([]);
        setErrorMessage('');
        setSubmitState('submitting');
        showLoadingMessage(trainerMode ? 'Updating LLND Review' : 'Submitting LLND');
        try {
            await Promise.resolve(onComplete(buildPayload(), trainerMode ? { decision } : undefined));
            setSubmitState(trainerMode ? 'saved' : 'idle');
            showSuccessMessage(trainerMode ? 'LLND review updated successfully' : 'LLND submitted successfully');
        } catch (error) {
            clearPageMessage();
            setSubmitState('idle');
            showWarningMessage('LLND could not be submitted. Please try again.');
        }
    };

    return (
        <div className='form-wrapper qld-branded-shell'>
            <div className='qld-page-brand'>
                <div>
                    <h1>White Card QLD Language, Literacy, Numeracy and Digital Assessment</h1>
                </div>
                <img src={Logo} alt='Upskill Institute' />
            </div>
            <div className='qldStudentSummaryGrid'>
                <div className='qldStudentSummaryWide'>
                    <Input type='text' bold errorMessage={hasError('studentName') ? 'This field is required.' : ''} errors={errors} name='studentName' value={studentName} setValue={setStudentName} label='Student Name' />
                </div>
                <div className='qldStudentSummaryNarrow'>
                    <Input type='text' bold disabled value={format(dateFirstPage, 'dd/MM/yyyy')} setValue={() => { }} label='Date' errors={[]} name='dateFirstPage' />
                </div>
                <div className='qldStudentSummaryFull'>
                    <Input type='text' bold disabled errorMessage={hasError('trainingCourse') ? 'This field is required.' : ''} errors={errors} name='trainingCourse' value={trainingCourse} setValue={setTrainingCourse} label='Training Course' />
                </div>
                <div className='qldStudentSummaryWide'>
                    <Input disabled type='text' bold errorMessage={hasError('trainerName') ? 'This field is required.' : ''} errors={errors} name='trainerName' value={trainerName} setValue={setTrainerName} label='Trainer Name' />
                </div>
                <div className='qldStudentSummaryNarrow'>
                    <Input disabled type='text' bold errorMessage={hasError('delivery') ? 'This field is required.' : ''} errors={errors} name='delivery' value={delivery} setValue={setDelivery} label='Delivery' />
                </div>
            </div>

            <p>This pre training language, literacy, numeracy and digital assessment is carried out to assess your capabilities. As an RTO, we always try to deliver the highest standard of service, and to do this, we need to ensure you have the knowledge to complete this training course.</p>
            <p><b>Instructions:<br /></b>Complete the following exercises to the best of your ability. You should try to complete all tasks and take your time and read each question carefully.</p>
            <p><b>This evaluation is not a test<br /></b>The information you provide about your writing and math skills will help us plan support throughout the program. This information is confidential and will not be used for any other purpose and will remain the property of Upskill Institute.</p>

            <b className={errors.includes('iCan') ? 'error' : ''} style={questionTitleStyles}>1. Tell us about your reading, writing and numeracy skills.</b>
            <table>
                <thead>
                    <tr>
                        <th>I can</th>
                        <th>Yes</th>
                        <th>Sometimes</th>
                        <th>No</th>
                    </tr>
                </thead>
                <tbody>
                    {iCanLabels.map((label, index) => (
                        <tr key={label}>
                            <td>{label}</td>
                            <td><Radio checked={'Yes' === iCan[index]} label='Yes' setValue={(value) => setICan([...iCan.map((row, idx) => (idx === index ? value : row))])} /></td>
                            <td><Radio checked={'Sometimes' === iCan[index]} label='Sometimes' setValue={(value) => setICan([...iCan.map((row, idx) => (idx === index ? value : row))])} /></td>
                            <td><Radio checked={'No' === iCan[index]} label='No' setValue={(value) => setICan([...iCan.map((row, idx) => (idx === index ? value : row))])} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {renderInlineError('iCan', 'Please answer every row in this table.')}

            <br />
            <b style={questionTitleStyles}>2. Look at the group of words below. Arrange these words in alphabetical order</b>
            <p>sun - shop - stop - strong - some - spot</p>
            <Input type='text' errors={errors} errorMessage={hasError('alphabetWordOne') ? 'This field is required.' : ''} name='alphabetWordOne' value={alphabetWordOne} setValue={setAlphabetWordOne} label='1.' />
            <Input type='text' errors={errors} errorMessage={hasError('alphabetWordTwo') ? 'This field is required.' : ''} name='alphabetWordTwo' value={alphabetWordTwo} setValue={setAlphabetWordTwo} label='2.' />
            <Input type='text' errors={errors} errorMessage={hasError('alphabetWordThree') ? 'This field is required.' : ''} name='alphabetWordThree' value={alphabetWordThree} setValue={setAlphabetWordThree} label='3.' />
            <Input type='text' errors={errors} errorMessage={hasError('alphabetWordFour') ? 'This field is required.' : ''} name='alphabetWordFour' value={alphabetWordFour} setValue={setAlphabetWordFour} label='4.' />
            <Input type='text' errors={errors} errorMessage={hasError('alphabetWordFive') ? 'This field is required.' : ''} name='alphabetWordFive' value={alphabetWordFive} setValue={setAlphabetWordFive} label='5.' />
            <Input type='text' errors={errors} errorMessage={hasError('alphabetWordSix') ? 'This field is required.' : ''} name='alphabetWordSix' value={alphabetWordSix} setValue={setAlphabetWordSix} label='6.' />

            <br />
            <b className={errors.includes('chosenIncorrectWords') ? 'error' : ''} style={questionTitleStyles}>3. Tick each incorrectly spelt words</b>
            <div
                className='checkboxGroup'
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: '12px 24px',
                    marginBottom: '12px',
                }}
            >
                {incorrectWords.map((word) => (
                    <Checkbox key={word} label={word} value={chosenIncorrectWords.includes(word)} setValue={(value) => toggleIncorrectWords(word, value)} />
                ))}
            </div>
            {renderInlineError('chosenIncorrectWords', 'Please select the incorrectly spelt words.')}

            <br />
            <br />
            <b style={questionTitleStyles}>4. Write a short paragraph about why you are interested in doing this course?</b>
            <textarea className={errors.includes('whyInterested') ? 'error' : ''} onChange={(event) => setWhyInterested(event.target.value)} value={whyInterested}></textarea>
            {renderInlineError('whyInterested')}

            <b style={questionTitleStyles}>5. How many grams in a kilogram? Tick the correct answer.</b>
            <div style={{ display: 'flex' }} className={'radioGroup ' + (errors.includes('grams') ? 'error' : '')}>
                {['100', '900', '1,000', '1,200'].map((word) => (
                    <div key={word} style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                        <Radio checked={grams === word} label={word} setValue={setGrams} />
                        <label style={{ marginTop: '5px' }}>{word}</label>
                    </div>
                ))}
            </div>
            {renderInlineError('grams', 'Please choose one answer.')}

            <br />
            <b style={questionTitleStyles}>6. If 10% of a number is 100, what is the number? Tick the correct answer.</b>
            <div style={{ display: 'flex' }} className={'radioGroup ' + (errors.includes('tenPercent') ? 'error' : '')}>
                {['10', '100', '1,000', '10,000'].map((word) => (
                    <div key={word} style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                        <Radio checked={tenPercent === word} label={word} setValue={setTenPercent} />
                        <label style={{ marginTop: '5px' }}>{word}</label>
                    </div>
                ))}
            </div>
            {renderInlineError('tenPercent', 'Please choose one answer.')}

            <br />
            <b style={questionTitleStyles}>7. One (1) metre (m) is equal to how many centimetres? Tick the correct answer</b>
            <div style={{ display: 'flex' }} className={'radioGroup ' + (errors.includes('oneMeter') ? 'error' : '')}>
                {['10,000', '1,000', '100', '10'].map((word) => (
                    <div key={word} style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                        <Radio checked={oneMeter === word} label={word} setValue={setOneMeter} />
                        <label style={{ marginTop: '5px' }}>{word}</label>
                    </div>
                ))}
            </div>
            {renderInlineError('oneMeter', 'Please choose one answer.')}

            <br />
            <b style={questionTitleStyles}>8. Calculate the heart beats per minute if a patient’s heart beats at 10 beats every 10 seconds. Tick the correct answer</b>
            <div style={{ display: 'flex' }} className={'radioGroup ' + (errors.includes('heartBeat') ? 'error' : '')}>
                {['20', '60', '100', '120'].map((word) => (
                    <div key={word} style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
                        <Radio checked={heartBeat === word} label={word} setValue={setHeartBeat} />
                        <label style={{ marginTop: '5px' }}>{word}</label>
                    </div>
                ))}
            </div>
            {renderInlineError('heartBeat', 'Please choose one answer.')}

            <br />
            <b style={questionTitleStyles}>9. What are two examples of a hazard on a construction site?</b>
            <textarea className={errors.includes('hazardEg') ? 'error' : ''} onChange={(event) => setHazardEg(event.target.value)} value={hazardEg}></textarea>
            {renderInlineError('hazardEg')}

            <br />
            <b style={questionTitleStyles}>10. What types of fire safety equipment are you familiar with?</b>
            <textarea className={errors.includes('fireSafety') ? 'error' : ''} onChange={(event) => setFireSafety(event.target.value)} value={fireSafety}></textarea>
            {renderInlineError('fireSafety')}

            <br />
            <b style={questionTitleStyles}>11. Why do you think safety is important when working on a construction site?</b>
            <textarea className={errors.includes('safetyImportance') ? 'error' : ''} onChange={(event) => setSafetyImportance(event.target.value)} value={safetyImportance}></textarea>
            {renderInlineError('safetyImportance')}

            <br />
            <b style={questionTitleStyles}>12. Oral Discussion</b>
            <p>Describe what you see in this picture below to your assessor. The Assessor will record your response below</p>
            <img src='https://upskillinstitute.com.au/docs/Oral-Discussion.jpg' alt='Oral discussion prompt' style={{ width: '100%' }} />
            <textarea className={errors.includes('oralDiscussion') ? 'error' : ''} onChange={(event) => setOralDiscussion(event.target.value)} value={oralDiscussion}></textarea>
            {renderInlineError('oralDiscussion')}

            {errorMessage && <p style={{ ...inlineErrorStyles, marginTop: '16px' }}>{errorMessage}</p>}

            {(!trainerMode || !hideTrainerDecisionActions) && <div style={{ display: 'flex', marginTop: '20px' }}>
                {trainerMode ? (
                    <>
                        <button style={{ backgroundColor: '#b42318' }} onClick={() => handleNext('not_satisfactory')}>
                            {submitState === 'submitting' ? 'Saving...' : 'Not Satisfactory'}
                        </button>
                        <button style={{ marginLeft: 'auto', display: 'flex', backgroundColor: '#1f7a1f' }} onClick={() => handleNext('satisfactory')}>
                            {submitState === 'submitting' ? 'Saving...' : 'Satisfactory'}
                        </button>
                    </>
                ) : (
                    <button style={{ marginLeft: 'auto', display: 'flex' }} onClick={() => handleNext()}>
                        {submitState === 'submitting' ? 'Saving...' : submitState === 'saved' ? 'Saved' : 'Submit'}
                    </button>
                )}
            </div>}
        </div>
    );
};

export default QldLlnStep;
